import {Flex} from 'src/design/atoms/Flex';
import {Group} from 'src/design/atoms/Group';
import React, {useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import type {
  SourceEntityRequest,
  TaskDefinitionRequest,
} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
  TaskDefinitionStateEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {
  useCreateTaskDefinitionMutation,
  useLazyGetTaskDefinitionDetailsQuery,
} from 'src/redux/queries/taskDefinition';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {TaskFeatureEditor} from 'src/app/tasks/TaskFeatureEditor';
import {buildCloneTaskRequest} from 'src/app/tasks/taskRequestTransport';
import {
  GetModuleEnumFromTaskType,
  GetUrlSectionFromTaskType,
  TaskPageTitleDict,
  TaskPillConfig,
} from 'src/app/tasks/taskHelpers';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useTaskBreadcrumbs} from 'src/shared/hooks/useTaskBreadcrumbs';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {capitalize} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IStateMachine} from 'src/types/StateMachine';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface CloneTaskPageProps {
  taskType: TaskTypeEnum;
  featureFlag: FeatureFlagEnum;
}

const CloneTaskPage: React.FC<CloneTaskPageProps> = ({
  taskType,
  featureFlag,
}) => {
  useSetDocumentTitle(capitalize(GetUrlSectionFromTaskType(taskType)));
  const module = GetModuleEnumFromTaskType(taskType);
  const navigate = useAppNavigate();

  const {setAlert} = useAlert();
  const {taskId} = useParams();
  const [editItem, setEditItem] = useState<any>();
  const [isLoadingTask, setIsLoadingTask] = useState<boolean>(true);
  const [isSavePending, setIsSavePending] = useState<boolean>(false);

  const clonedTaskId = useRef<number | null>(null);
  const transitionUrl = `task-definition/${clonedTaskId.current}/transition`;

  const [triggerGetDetails] = useLazyGetTaskDefinitionDetailsQuery();
  const [createTaskDefinition] = useCreateTaskDefinitionMutation();

  useEffect(() => {
    const getData = async () => {
      if (taskId === undefined || isNaN(+taskId)) {
        return;
      }

      try {
        const response = await triggerGetDetails(+taskId).unwrap();

        const item = {
          ...response,
          internalIdentifier: undefined,
          state: TaskDefinitionStateEnum.Draft,
          contentPdfUrl: response.contentPdfUrl ?? undefined,
          dueInDays: response.dueInDays
            ? String(response.dueInDays)
            : undefined,
          ...{
            sourceEntities: {
              selectAllEntitiesType: response.metadata?.selectAllEntityType,
              entities: response.sourceEntities.map(entity => ({
                entityId: entity.entityId,
                entityType: entity.entityType,
              })),
            } as SourceEntityRequest,
          },
        };

        setEditItem(item);
      } catch (error: any) {
        setAlert(parseErrorMessage(error), 'error');
      }
      setIsLoadingTask(false);
    };

    void getData();
  }, [taskId]);

  const onSubmitted = () =>
    navigate(
      PathUtility.TaskView(taskType, String(clonedTaskId.current), 'content'),
      {replace: true}
    );

  const breadcrumbs = useTaskBreadcrumbs({
    taskType,
    mode: 'clone',
    taskId,
    itemSubject: editItem?.subject,
  });

  const pills = editItem
    ? TaskPillConfig(
        editItem.state,
        editItem.taskType,
        undefined,
        editItem.approvalState
      )
    : [];

  const [machineBase] = useStateMachine({
    url: ApiEndpoints.getTaskDefinitionStateMachine,
  });
  const machine = machineBase
    ? ({
        states: machineBase.states,
        transitions: machineBase.transitions.map(t => ({
          ...t,
          title: `Save & ${t.title}`,
        })),
      } as IStateMachine)
    : undefined;

  return (
    <TaskFeatureEditor
      taskType={taskType}
      taskState={editItem?.state}
      initialData={editItem ? toFormDataV2(editItem) : undefined}
      resetKey={editItem?.id}>
      {({formApi, form, getFeatureFields}) => {
        const submit = async (request?: TaskDefinitionRequest) => {
          if (!(await formApi.validateForm())) {
            return {continue: false};
          }
          setIsSavePending(true);
          const body = buildCloneTaskRequest({
            item: editItem,
            featureFields: getFeatureFields(),
            transitionFields: request,
          });
          try {
            const resp = await createTaskDefinition(body).unwrap();
            setAlert(
              `${TaskPageTitleDict[taskType].singular} was successfully created`,
              'success'
            );
            clonedTaskId.current = resp.id;
            return {
              continue: true,
              overrideTransitionBaseUrl: `task-definition/${resp.id}/transition`,
            };
          } catch (error) {
            setAlert(parseErrorMessage(error), 'error');
            setIsSavePending(false);
            return {continue: false};
          }
        };
        const handleSave = async () => {
          const result = await submit();
          if (result.continue) onSubmitted();
        };
        const handleTransitioning = () =>
          submit({skipOnUpdatedSideEffect: true});

        return (
          <ContentWrapper featureFlag={featureFlag}>
            {!isLoadingTask ? (
              <Flex direction="column" w="100%">
                <BackBar
                  fallbackTo={PathUtility.TaskView(
                    taskType,
                    taskId ?? '',
                    'content'
                  )}
                  breadcrumbs={breadcrumbs}
                />
                <PageHeader>
                  <PageHeader.Title>
                    {editItem
                      ? `Clone ${TaskPageTitleDict[taskType].singular}`
                      : 'Item not found'}
                  </PageHeader.Title>
                  {pills.length > 0 && (
                    <PageHeader.Badges>
                      {pills.map((pill, index) => (
                        <PageHeader.Badge
                          key={index}
                          color={pill.color}
                          variant="light">
                          {pill.text}
                        </PageHeader.Badge>
                      ))}
                    </PageHeader.Badges>
                  )}
                  <PageHeader.Badges>
                    <PageHeader.Badge color="blue" variant="light">
                      Cloning
                    </PageHeader.Badge>
                  </PageHeader.Badges>
                  <PageHeader.Actions>
                    <PageHeader.Action
                      variant="save"
                      loading={isSavePending}
                      onClick={handleSave}>
                      Save
                    </PageHeader.Action>
                  </PageHeader.Actions>
                </PageHeader>
                {form}

                <Group align="start">
                  {machine ? (
                    <FormTransitionBar
                      preTransitionButtonNodes={[
                        <SaveButton
                          loading={isSavePending}
                          onClick={handleSave}
                          mr="md"
                        />,
                      ]}
                      onTransitioning={handleTransitioning}
                      module={module}
                      stateMachine={machine}
                      stateId={TaskDefinitionStateEnum.Draft}
                      transitionBaseUrl={transitionUrl}
                      onRefreshTransition={onSubmitted}
                      invalidateTagsOnComplete={[
                        'TaskDefinitions',
                        'TaskAssignments',
                      ]}
                    />
                  ) : (
                    <SaveButton loading={isSavePending} onClick={handleSave} />
                  )}
                </Group>
              </Flex>
            ) : (
              <TableBodyRender
                isLoading={isLoadingTask}
                hasData={editItem !== undefined}
              />
            )}
          </ContentWrapper>
        );
      }}
    </TaskFeatureEditor>
  );
};

// Register the routes with the new system
[
  {
    type: TaskTypeEnum.Approval,
    featureFlag: FeatureFlagEnum.Approvals,
    module: ModuleEnum.Approvals,
  },
  {
    type: TaskTypeEnum.ToolboxTalk,
    featureFlag: FeatureFlagEnum.ToolboxTalks,
    module: ModuleEnum.ToolboxTalks,
  },
  {
    type: TaskTypeEnum.Form,
    featureFlag: FeatureFlagEnum.Forms,
    module: ModuleEnum.AssignedForms,
  },
].forEach(item => {
  routeRegistry.registerPageRoute({
    backNavigation: 'form',
    path: PathUtility.TaskClone(item.type, ':taskId'),
    element: (
      <CloneTaskPage taskType={item.type} featureFlag={item.featureFlag} />
    ),
    module: item.module,
    operation: PermissionOperationEnum.Create,
    isPrivate: true,
  });
});

export default CloneTaskPage;
